import { faArchive, faEllipsis, faExternalLink, faInbox } from '@fortawesome/pro-regular-svg-icons'
import { faEnvelope, faEnvelopeOpen } from '@fortawesome/pro-solid-svg-icons'

import { isNotNull } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import type { ConversationMenuConversationFragment } from '@/client/services/hasura/generated/graphql'
import * as url from '@/client/services/url'
import { getUserUrl } from '@/client/services/url'

import { useToaster } from '@/components/Toast/useToaster'
import Icon from '@/components/UI/Icon/Icon'
import Menu from '@/components/Worlds/UI/Menu/Menu'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

export default function ConversationMenu({
  conversation,
  showAdminOptions = false,
}: {
  conversation: ConversationMenuConversationFragment
  showAdminOptions?: boolean
}): JSX.Element | null {
  const currentUser = useUser()
  const toast = useToaster()

  const {
    series,
    type,
    session,
    conversation_users: conversationUsers,
    current_conversation_user: [currentConversationUser],
    last_message: [lastMessage],
  } = conversation

  const isDirectConversation = type === 'direct'

  const otherUsers = conversationUsers
    .map(item => item.user)
    .filter(user => user.id !== currentUser.id)

  const isUnread =
    lastMessage != null &&
    currentConversationUser != null &&
    (currentConversationUser.last_read_at == null ||
      lastMessage.created_at > currentConversationUser.last_read_at)

  const [handleToggleArchive, isToggleArchiveLoading] = useAPIWrapper(
    async () => {
      if (currentConversationUser == null) {
        return
      }
      const isArchived = currentConversationUser.archived_at == null
      toast({
        status: 'success',
        title: isArchived ? 'Archiving conversation...' : 'Unarchived conversation...',
      })
      await RPC.changeConversationArchiveStatus({
        conversationId: conversation.id,
        isArchived,
      })
      toast({
        status: 'success',
        title: isArchived ? 'Archived conversation.' : 'Unarchived conversation.',
      })
    },
    [conversation.id, currentConversationUser, toast],
    'Change archive status'
  )

  const [handleToggleReadStatus, isToggleReadStatusLoading] = useAPIWrapper(
    async () => {
      await RPC.updateConversationReadStatus({
        conversationId: conversation.id,
        isUnread: !isUnread,
      })
    },
    [isUnread, conversation.id],
    'Change read status'
  )

  const menuItems: MenuItemType[] = [
    isDirectConversation && otherUsers.length > 0
      ? {
          icon: faExternalLink,
          label: 'View Profile',
          href: getUserUrl({
            userId: otherUsers[0].id,
            username: otherUsers[0].username,
          }),
        }
      : null,
    series != null
      ? {
          icon: faExternalLink,
          label: 'View Series',
          href: url.getSeriesUrlById(series.id),
        }
      : null,
    session != null
      ? {
          icon: faExternalLink,
          label: 'View Session',
          href: url.getSessionUrlById(session.id),
        }
      : null,
    showAdminOptions
      ? {
          icon: faExternalLink,
          label: 'View Admin Page',
          href: url.getAdminConversationUrl(conversation.id),
        }
      : null,
    currentConversationUser == null
      ? null
      : {
          icon: currentConversationUser.archived_at == null ? faArchive : faInbox,
          label:
            currentConversationUser.archived_at == null
              ? 'Archive Conversation'
              : 'Unarchive Conversation',
          onClick: () => handleToggleArchive(),
          disabled: isToggleArchiveLoading,
        },
    currentConversationUser == null
      ? null
      : {
          icon: isUnread ? faEnvelopeOpen : faEnvelope,
          label: isUnread ? 'Mark as Read' : 'Mark as Unread',
          onClick: handleToggleReadStatus,
          disabled: isToggleReadStatusLoading,
        },
  ].filter(isNotNull)

  return menuItems.length === 0 ? null : (
    <Menu
      buttonProps={{
        'aria-label': 'Conversation Actions',
        icon: <Icon faIcon={faEllipsis} size="md" color="fg.muted" />,
      }}
      itemGroups={[menuItems]}
    />
  )
}
